import { useEffect, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import {
  Activity,
  RefreshCw,
  Loader2,
  Unplug,
  Wifi,
} from "lucide-react";

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { SortableTableHead } from "@/components/ui/sortable-table-head";
import { Badge } from "@/components/ui/badge";
import { useSortableData } from "@/hooks/useSortableData";
import { useAuth } from "@/contexts/AuthContext";
import { usePermissions } from "@/hooks/usePermissions";
import { formatTime } from "@/lib/dateFormat";
import { ftpApi } from "@/services/api/ftp";
import type { FtpConnection } from "@/services/api/ftp";
import { toast } from "sonner";

export const Route = createFileRoute("/portal/ftp/connections")({
  component: FtpConnectionsPage,
});

function FtpConnectionsPage() {
  const { token } = useAuth();
  const { can } = usePermissions();
  // FT28: Casbin grants org_admin (create|read|update|delete) but NOT `manage`,
  // and the backend DELETE authorises them — so gate Disconnect on `delete`, not
  // `manage` (which hid the button from admins who could actually use it).
  const canDisconnect = can("delete", "ftp");

  const [connections, setConnections] = useState<FtpConnection[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [disconnecting, setDisconnecting] = useState<string | null>(null);
  const [disconnectSubmitting, setDisconnectSubmitting] = useState(false);

  useEffect(() => {
    if (!token) return;
    loadConnections();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  async function loadConnections() {
    setLoading(true);
    try {
      const response = await ftpApi.listConnections(token!);
      setConnections(response.connections || []);
    } catch {
      toast.error("Failed to load connections");
    } finally {
      setLoading(false);
    }
  }

  async function handleRefresh() {
    setRefreshing(true);
    try {
      const response = await ftpApi.listConnections(token!);
      setConnections(response.connections || []);
      toast.success("Connections refreshed");
    } catch {
      toast.error("Failed to refresh connections");
    } finally {
      setRefreshing(false);
    }
  }

  async function handleDisconnect() {
    if (!token || !disconnecting) return;
    setDisconnectSubmitting(true);
    try {
      await ftpApi.closeConnection(token, disconnecting);
      toast.success("Connection closed");
      setDisconnecting(null);
      loadConnections();
    } catch {
      toast.error("Failed to close connection");
    } finally {
      setDisconnectSubmitting(false);
    }
  }

  // Client-side sorting
  const { sortedData, requestSort, getSortDirection } = useSortableData(connections, {
    initialSort: { key: "username", direction: "asc" },
  });

  function formatDuration(connectionTime: string): string {
    if (!connectionTime) return "-";
    const start = new Date(connectionTime);
    const now = new Date();
    const diffMs = now.getTime() - start.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);

    if (diffHours > 0) {
      return `${diffHours}h ${diffMins % 60}m`;
    }
    return `${diffMins}m`;
  }

  function formatConnectionTime(dateStr: string): string {
    if (!dateStr) return "-";
    return formatTime(dateStr);
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold text-slate-900">
            Active Connections
          </h1>
          <p className="mt-2 text-lg text-slate-600">
            Monitor active SFTP sessions in real-time
          </p>
        </div>
        <Button
          variant="outline"
          onClick={handleRefresh}
          disabled={refreshing}
        >
          <RefreshCw
            className={`mr-2 h-4 w-4 ${refreshing ? "animate-spin" : ""}`}
          />
          Refresh
        </Button>
      </div>

      <Card>
        <CardHeader className="py-4">
          <CardTitle className="flex items-center gap-2 text-base">
            <Activity className="h-4 w-4" />
            Connections ({connections.length})
          </CardTitle>
          <CardDescription>
            Currently active SFTP sessions
          </CardDescription>
        </CardHeader>
        <CardContent className="pt-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : connections.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <Wifi className="mb-4 h-12 w-12 text-muted-foreground/30" />
              <p className="text-muted-foreground">
                No active SFTP connections
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <SortableTableHead
                    direction={getSortDirection("username")}
                    onSort={() => requestSort("username")}
                  >
                    Username
                  </SortableTableHead>
                  <SortableTableHead
                    direction={getSortDirection("remote_address")}
                    onSort={() => requestSort("remote_address")}
                  >
                    Remote Address
                  </SortableTableHead>
                  <SortableTableHead
                    direction={getSortDirection("protocol")}
                    onSort={() => requestSort("protocol")}
                  >
                    Protocol
                  </SortableTableHead>
                  <SortableTableHead
                    direction={getSortDirection("client_version")}
                    onSort={() => requestSort("client_version")}
                  >
                    Client
                  </SortableTableHead>
                  <SortableTableHead
                    direction={getSortDirection("connection_time")}
                    onSort={() => requestSort("connection_time")}
                  >
                    Connected At
                  </SortableTableHead>
                  <SortableTableHead
                    direction={null}
                    onSort={() => {}}
                    disabled
                  >
                    Duration
                  </SortableTableHead>
                  <SortableTableHead disabled direction={null} onSort={() => {}}>
                    Active Transfers
                  </SortableTableHead>
                  {canDisconnect && <SortableTableHead disabled direction={null} onSort={() => {}} className="w-[80px]">{null}</SortableTableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedData.map((conn) => (
                  <TableRow key={conn.connection_id}>
                    <TableCell className="font-medium">
                      {conn.username}
                    </TableCell>
                    <TableCell className="font-mono text-xs text-muted-foreground">
                      {conn.remote_address}
                    </TableCell>
                    <TableCell>
                      <Badge variant="secondary">{conn.protocol}</Badge>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {conn.client_version || "-"}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {formatConnectionTime(conn.connection_time)}
                    </TableCell>
                    <TableCell className="text-sm">
                      {formatDuration(conn.connection_time)}
                    </TableCell>
                    <TableCell>
                      {conn.active_transfers?.length > 0 ? (
                        <Badge variant="info">
                          {conn.active_transfers.length} active
                        </Badge>
                      ) : (
                        <span className="text-sm text-muted-foreground">
                          Idle
                        </span>
                      )}
                    </TableCell>
                    {canDisconnect && (
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() =>
                            setDisconnecting(conn.connection_id)
                          }
                          className="text-destructive hover:text-destructive"
                        >
                          <Unplug className="mr-1 h-4 w-4" />
                          Close
                        </Button>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Disconnect Confirmation */}
      <AlertDialog
        open={!!disconnecting}
        onOpenChange={(open) => !open && setDisconnecting(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Close Connection?</AlertDialogTitle>
            <AlertDialogDescription>
              This will immediately disconnect the SFTP session. Any in-progress
              file transfers will be interrupted.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={disconnectSubmitting}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDisconnect}
              disabled={disconnectSubmitting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {disconnectSubmitting && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Close Connection
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
